
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui';
import { askGemini } from '../../lib/gemini';
import { Sparkles, Copy, RefreshCw, FileText } from 'lucide-react';

export const AIMatchReportGenerator = ({ matches, teams, sportType = 'goals' }) => {
    const { t } = useTranslation()
    const [report, setReport] = useState('');
    const [loading, setLoading] = useState(false);

    // Filter played matches
    const playedMatches = matches.filter(m =>
        m.score_home !== null && m.score_home !== undefined &&
        m.score_away !== null && m.score_away !== undefined
    );

    const generateReport = async () => {
        if (playedMatches.length === 0) {
            setReport(t('matches_page.no_matches_calculated'));
            return;
        }

        setLoading(true);
        try {
            // Build context string
            let matchesText = playedMatches.map(m => {
                const homeTeam = teams.find(t => t.id === m.home_team_id)?.name || 'Thuis';
                const awayTeam = teams.find(t => t.id === m.away_team_id)?.name || 'Uit';
                return `${homeTeam} vs ${awayTeam}: ${m.score_home}-${m.score_away}`;
            }).join('\n');

            const prompt = `
            Schrijf een enthousiast, sportief nieuwsbericht/verslag over de recente speelronde.
            Sport Type: ${sportType === 'goals' ? 'Voetbal/Hockey (Doelpunten)' : 'Tennis/Padel (Sets)'}.
            
            Uitslagen:
            ${matchesText}

            Schrijfstijl: Journalistiek, positief, sensatie. Gebruik emojis.
            Structureer het met een kop, een intro, en hoogtepunten.
            Focus op de opvallende uitslagen (grote winst of nipte overwinning).
            `;

            const result = await askGemini(prompt);
            setReport(result);
        } catch (error) {
            console.error(error);
            setReport(t('common.gen_error') + error.message);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(report);
        alert(t('matches_page.alert_copied') || t('presentation_page.alert_copied'));
    };

    return (
        <div className="space-y-4">
            <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-6 rounded-2xl border border-purple-100">
                <div className="flex items-center gap-3 mb-4">
                    <div className="bg-white p-2 rounded-lg shadow-sm">
                        <Sparkles className="text-purple-600" size={24} />
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900 text-lg">{t('matches_page.ai_report_title')}</h3>
                        <p className="text-sm text-gray-500">
                            {playedMatches.length} gespeelde wedstrijden gevonden
                        </p>
                    </div>
                </div>

                <p className="text-sm text-gray-600 mb-6 font-medium">
                    Laat de AI een samenvatting schrijven voor je website, nieuwsbrief of groepsapp.
                </p>

                <Button
                    onClick={generateReport}
                    disabled={loading || playedMatches.length === 0}
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 shadow-lg hover:shadow-xl transition-all"
                    icon={loading ? <RefreshCw className="animate-spin" /> : <FileText />}
                >
                    {loading ? t('matches_page.ai_writing') : t('matches_page.ai_report_btn')}
                </Button>
            </div>

            {report && (
                <div className="animate-slideUp">
                    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm relative group">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={copyToClipboard}
                            className="absolute top-4 right-4 text-gray-400 hover:text-purple-600 bg-white shadow-sm border border-gray-100"
                        >
                            <Copy size={16} className="mr-2" /> {t('matches_page.btn_copy') || t('presentation_page.btn_copy')}
                        </Button>
                        <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-line font-medium leading-relaxed">
                            {report}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
